/**
 * RegistroFinancieroER — unidad atómica de la Fase 3 del ER v2 (fuente única).
 *
 * Reemplaza progresivamente los `Array(12)` que hoy producen los hooks de ingreso
 * (`usePublicidadCalcs`, `useContratosCalcs`, `useBTLCalcs`, `useHardwareCalcs`,
 * `useGestionCalcs`) y `costosER.js`: cada documento/partida se transporta con su propia
 * fecha (o `null` explícito si no tiene una propia) en vez de aplastarse de entrada a un
 * total mensual — eso es lo que permite, después, convertir cada uno a US$ con el TC de
 * SU fecha (Criterio 1) en vez de un TC único por mes.
 *
 * Ver docs/decisiones/2026-10-04-reingenieria-ebitda-fase3-diseno.md §1.1-1.2.
 */

// "COSTO_SII" (PASO 3.5): las facturas de `costosER.js` (SII/externas/boletas) se clasifican por
// `cuenta_er`, no por bloque — su bloque final (A/C variable, o FIJO) lo decide el mapeo
// cuenta→bloque que hoy vive en `index.js` (`camposCostosFijos`/el split Publicidad-Gestión de
// `contenidos_digitales`), AGUAS ABAJO de este registro. Forzar un bloque A/B/C/D/FIJO acá sería
// adivinar una clasificación que todavía no se resolvió — "COSTO_SII" es honesto: dice "esto es
// un costo del SII, su bloque se resuelve después", nunca "elegí un bloque al azar".
export const BLOQUES_ER = ["A", "B", "C", "D", "FIJO", "COSTO_SII"];
export const PAISES_ER = ["Chile", "Perú"];

/**
 * Construye un RegistroFinancieroER validado. Lanza si falta algo obligatorio — un registro
 * mal formado es peor que un error temprano (10.177): correría en silencio por toda la cadena
 * de agregación y conversión en vez de avisar en el momento en que se crea.
 *
 * `ano`/`mesIndice` van SIEMPRE explícitos (no se derivan de `fecha`): son la ubicación del
 * registro en la tabla (a qué mes pertenece), que es una pregunta distinta de "qué TC usar"
 * (eso lo decide `fecha`, en conversionER.js). Para la inmensa mayoría de los registros ambas
 * cosas coinciden, pero no siempre (ej. una partida sin fecha propia igual pertenece a un mes).
 *
 * @param {object} p
 * @param {string|null} [p.fecha] - "YYYY-MM-DD" o null si la partida no tiene fecha propia.
 * @param {number} p.ano - Año al que pertenece el registro (para ubicarlo en la tabla).
 * @param {number} p.mesIndice - Mes 0-11 al que pertenece (para ubicarlo en la tabla).
 * @param {number} p.montoLocal - Monto en moneda LOCAL del país. Signo: +ingreso/comisión, −costo.
 * @param {"A"|"B"|"C"|"D"|"FIJO"} p.bloque
 * @param {string} p.concepto - Nombre de la línea ("ingreso", "comision_agencia", "software_ds", ...).
 * @param {"Chile"|"Perú"} p.pais
 * @param {object|null} [p.meta] - Id/origen del documento de donde sale, para el modal de detalle.
 * @returns {object} RegistroFinancieroER
 */
export const crearRegistroFinancieroER = ({
  fecha = null,
  ano,
  mesIndice,
  montoLocal,
  bloque,
  concepto,
  pais,
  meta = null,
}) => {
  if (fecha !== null && typeof fecha !== "string") {
    throw new Error(
      `crearRegistroFinancieroER: fecha inválida (${JSON.stringify(fecha)}), esperado "YYYY-MM-DD" o null`
    );
  }
  if (fecha !== null && !/^\d{4}-\d{2}-\d{2}$/.test(fecha)) {
    throw new Error(`crearRegistroFinancieroER: fecha con formato inválido "${fecha}" (esperado YYYY-MM-DD)`);
  }
  if (!Number.isInteger(ano)) throw new Error(`crearRegistroFinancieroER: ano inválido (${ano})`);
  if (!Number.isInteger(mesIndice) || mesIndice < 0 || mesIndice > 11) {
    throw new Error(`crearRegistroFinancieroER: mesIndice inválido (${mesIndice}), esperado 0-11`);
  }
  if (typeof montoLocal !== "number" || Number.isNaN(montoLocal)) {
    throw new Error(`crearRegistroFinancieroER: montoLocal inválido (${montoLocal}) para concepto "${concepto}"`);
  }
  if (!BLOQUES_ER.includes(bloque)) throw new Error(`crearRegistroFinancieroER: bloque inválido "${bloque}"`);
  if (!concepto) throw new Error("crearRegistroFinancieroER: concepto es obligatorio");
  if (!PAISES_ER.includes(pais)) throw new Error(`crearRegistroFinancieroER: pais inválido "${pais}"`);

  return { fecha, ano, mesIndice, montoLocal, bloque, concepto, pais, meta };
};

/** Suma de `montoLocal` de un array de registros. */
export const sumarMontoLocal = (registros) => (registros || []).reduce((acc, r) => acc + (r.montoLocal || 0), 0);

/** Filtra registros de un (pais, ano[, mesIndice]) exacto. */
export const registrosDe = (registros, { pais, ano, mesIndice }) =>
  (registros || []).filter(
    (r) => r.pais === pais && r.ano === ano && (mesIndice === undefined || r.mesIndice === mesIndice)
  );

/**
 * Agrega un array de RegistroFinancieroER a un Array(12) de `montoLocal` por mes, para un
 * (pais, ano) dado. Usado por los tests de paridad (3.2-3.5): el array resultante tiene que
 * ser idéntico, celda a celda, al array que hoy produce el hook viejo equivalente.
 */
export const agregarAMesesLocal = (registros, { pais, ano }) => {
  const meses = Array(12).fill(0);
  (registros || []).forEach((r) => {
    if (r.pais !== pais || r.ano !== ano) return;
    meses[r.mesIndice] += r.montoLocal;
  });
  return meses;
};

/**
 * Igual que `agregarAMesesLocal` pero en US$ (lee `montoUSD`, que debe venir ya resuelto por
 * `convertirRegistroAUSD`/`convertirRegistrosAUSD` de `conversionER.js` — esta función NO
 * convierte, solo agrega lo que ya está convertido).
 */
export const agregarAMesesUSD = (registros, { pais, ano }) => {
  const meses = Array(12).fill(0);
  (registros || []).forEach((r) => {
    if (r.pais !== pais || r.ano !== ano) return;
    meses[r.mesIndice] += r.montoUSD || 0;
  });
  return meses;
};
